"use client"

import { useState, useCallback } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Copy, Check, Trash2, ArrowRightLeft } from "lucide-react"
import { toast } from "sonner"

type EscapeType = "json" | "javascript" | "html" | "url" | "xml" | "csv" | "sql"
type Mode = "escape" | "unescape"

interface EscapeOption {
  value: EscapeType
  label: string
  description: string
}

const escapeOptions: EscapeOption[] = [
  { value: "json", label: "JSON String", description: "Escape for JSON string values" },
  { value: "javascript", label: "JavaScript", description: "Escape for JS string literals" },
  { value: "html", label: "HTML", description: "Escape HTML special characters" },
  { value: "url", label: "URL", description: "Percent-encode for URLs" },
  { value: "xml", label: "XML", description: "Escape XML special characters" },
  { value: "csv", label: "CSV", description: "Escape for CSV fields" },
  { value: "sql", label: "SQL String", description: "Escape for SQL string literals" },
]

function escapeJson(str: string): string {
  return JSON.stringify(str).slice(1, -1)
}

function unescapeJson(str: string): string {
  try {
    return JSON.parse(`"${str}"`)
  } catch {
    return str
  }
}

function escapeJavaScript(str: string): string {
  return str
    .replace(/\\/g, "\\\\")
    .replace(/'/g, "\\'")
    .replace(/"/g, '\\"')
    .replace(/\n/g, "\\n")
    .replace(/\r/g, "\\r")
    .replace(/\t/g, "\\t")
    .replace(/\f/g, "\\f")
    .replace(/\v/g, "\\v")
    .replace(/\0/g, "\\0")
}

function unescapeJavaScript(str: string): string {
  return str
    .replace(/\\0/g, "\0")
    .replace(/\\v/g, "\v")
    .replace(/\\f/g, "\f")
    .replace(/\\t/g, "\t")
    .replace(/\\r/g, "\r")
    .replace(/\\n/g, "\n")
    .replace(/\\"/g, '"')
    .replace(/\\'/g, "'")
    .replace(/\\\\/g, "\\")
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
}

function unescapeHtml(str: string): string {
  return str
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&gt;/g, ">")
    .replace(/&lt;/g, "<")
    .replace(/&amp;/g, "&")
}

function escapeUrl(str: string): string {
  return encodeURIComponent(str)
}

function unescapeUrl(str: string): string {
  try {
    return decodeURIComponent(str)
  } catch {
    return str
  }
}

function escapeXml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;")
}

function unescapeXml(str: string): string {
  return str
    .replace(/&apos;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&gt;/g, ">")
    .replace(/&lt;/g, "<")
    .replace(/&amp;/g, "&")
}

function escapeCsv(str: string): string {
  if (str.includes(",") || str.includes('"') || str.includes("\n") || str.includes("\r")) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

function unescapeCsv(str: string): string {
  if (str.startsWith('"') && str.endsWith('"')) {
    return str.slice(1, -1).replace(/""/g, '"')
  }
  return str
}

function escapeSql(str: string): string {
  return str.replace(/'/g, "''").replace(/\\/g, "\\\\")
}

function unescapeSql(str: string): string {
  return str.replace(/\\\\/g, "\\").replace(/''/g, "'")
}

function escape(str: string, type: EscapeType): string {
  switch (type) {
    case "json": return escapeJson(str)
    case "javascript": return escapeJavaScript(str)
    case "html": return escapeHtml(str)
    case "url": return escapeUrl(str)
    case "xml": return escapeXml(str)
    case "csv": return escapeCsv(str)
    case "sql": return escapeSql(str)
    default: return str
  }
}

function unescape(str: string, type: EscapeType): string {
  switch (type) {
    case "json": return unescapeJson(str)
    case "javascript": return unescapeJavaScript(str)
    case "html": return unescapeHtml(str)
    case "url": return unescapeUrl(str)
    case "xml": return unescapeXml(str)
    case "csv": return unescapeCsv(str)
    case "sql": return unescapeSql(str)
    default: return str
  }
}

const sampleText = `Hello "World"!
This has 'quotes' and <special> & characters.
Line breaks and	tabs too.`

export function StringEscape() {
  const [mode, setMode] = useState<Mode>("escape")
  const [escapeType, setEscapeType] = useState<EscapeType>("json")
  const [input, setInput] = useState("")
  const [output, setOutput] = useState("")
  const [copied, setCopied] = useState(false)

  const convert = useCallback(() => {
    if (!input) {
      setOutput("")
      return
    }

    try {
      const result = mode === "escape" ? escape(input, escapeType) : unescape(input, escapeType)
      setOutput(result)
      toast.success(mode === "escape" ? "Escaped successfully" : "Unescaped successfully")
    } catch {
      toast.error("Conversion failed")
    }
  }, [input, mode, escapeType])

  const toggleMode = () => {
    setMode((prev) => (prev === "escape" ? "unescape" : "escape"))
    setInput(output)
    setOutput("")
  }

  const loadSample = () => {
    setInput(sampleText)
    setOutput("")
  }

  const clear = () => {
    setInput("")
    setOutput("")
  }

  const copyOutput = async () => {
    if (!output) return
    await navigator.clipboard.writeText(output)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    toast.success("Copied to clipboard")
  }

  const selectedOption = escapeOptions.find((o) => o.value === escapeType)

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>String {mode === "escape" ? "Escape" : "Unescape"}</CardTitle>
          <CardDescription>
            {mode === "escape"
              ? "Escape special characters for different contexts"
              : "Unescape encoded strings back to original"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Options */}
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-2">
              <Label>Escape Format</Label>
              <Select value={escapeType} onValueChange={(v) => setEscapeType(v as EscapeType)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {escapeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2 ml-auto">
              <Button variant="outline" size="sm" onClick={loadSample}>
                Load Sample
              </Button>
              <Button variant="outline" size="sm" onClick={clear}>
                <Trash2 className="h-4 w-4 mr-1" />
                Clear
              </Button>
            </div>
          </div>

          {/* Format description */}
          {selectedOption && (
            <p className="text-sm text-muted-foreground">
              {selectedOption.description}
            </p>
          )}

          {/* Input */}
          <div className="space-y-2">
            <Label htmlFor="input">
              {mode === "escape" ? "Original String" : "Escaped String"}
            </Label>
            <Textarea
              id="input"
              placeholder={
                mode === "escape"
                  ? "Enter text to escape..."
                  : "Enter escaped text to unescape..."
              }
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="font-mono text-sm min-h-[150px]"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button onClick={convert} disabled={!input.trim()}>
              {mode === "escape" ? "Escape" : "Unescape"}
            </Button>
            <Button variant="outline" onClick={toggleMode}>
              <ArrowRightLeft className="h-4 w-4 mr-2" />
              Switch to {mode === "escape" ? "Unescape" : "Escape"}
            </Button>
          </div>

          {/* Output */}
          {output && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>
                  {mode === "escape" ? "Escaped String" : "Original String"}
                </Label>
                <Button variant="ghost" size="sm" onClick={copyOutput}>
                  {copied ? (
                    <Check className="h-4 w-4 mr-1 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4 mr-1" />
                  )}
                  Copy
                </Button>
              </div>
              <Textarea
                value={output}
                readOnly
                className="font-mono text-sm min-h-[150px] bg-muted"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reference */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Escape Reference</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-3">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium mb-2">Common Escape Sequences</h4>
              <ul className="space-y-1 text-muted-foreground font-mono text-xs">
                <li>\n - New line</li>
                <li>\t - Tab</li>
                <li>\r - Carriage return</li>
                <li>\\ - Backslash</li>
                <li>{"\\\""} - Double quote</li>
                <li>{"\\'"} - Single quote</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">Use Cases</h4>
              <ul className="space-y-1 text-muted-foreground text-xs">
                <li><strong>JSON:</strong> API requests, config files</li>
                <li><strong>HTML:</strong> Prevent XSS, display special chars</li>
                <li><strong>URL:</strong> Query parameters, path segments</li>
                <li><strong>SQL:</strong> Prevent SQL injection</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
